<template>
  <div v-if="email">
    <!-- The preview column wraps under the form on narrow screens. -->
    <div class="flex items-start gap-6" style="flex-wrap:wrap;">
      <!-- left form card -->
      <div class="console-card" style="flex:999 1 560px; min-width:0; overflow:hidden;">
        <div style="padding:16px;">
          <!-- ===== Details ===== -->
          <div v-show="activeTab === 'details'" class="flex flex-col gap-3.5">
            <div>
              <div class="console-label" style="margin-bottom:7px;">Name</div>
              <input v-model="email.name" class="console-input">
            </div>
            <div class="grid grid-cols-2 gap-3.5">
              <div>
                <div class="console-label" style="margin-bottom:7px;">Organization</div>
                <select v-model="email.parent" class="console-input">
                  <option v-for="o in orgOptions" :key="o.value" :value="o.value">{{ o.label }}</option>
                </select>
              </div>
              <div>
                <div class="console-label" style="margin-bottom:7px;">Alias</div>
                <input v-model="email.alias" class="console-input mono">
              </div>
            </div>
            <div class="grid grid-cols-2 gap-3.5">
              <div>
                <div class="console-label" style="margin-bottom:7px;">Deploy date</div>
                <input v-model="email.deployDate" type="date" class="console-input mono">
              </div>
              <div>
                <div class="console-label" style="margin-bottom:7px;">Created date</div>
                <input v-model="email.date" type="date" class="console-input mono">
              </div>
            </div>
            <div>
              <div class="console-label" style="margin-bottom:7px;">Subject</div>
              <input v-model="email.subject" class="console-input">
            </div>
            <div>
              <div class="console-label" style="margin-bottom:7px;">Preview text</div>
              <textarea v-model="email.previewText" rows="2" class="console-input" style="height:auto; padding:8px 12px; resize:vertical;" />
            </div>
            <div class="grid grid-cols-2 gap-3.5">
              <div>
                <div class="console-label" style="margin-bottom:7px;">Base template</div>
                <input v-model="email.wrapper" class="console-input mono">
              </div>
              <div>
                <div class="console-label" style="margin-bottom:7px;">Link params</div>
                <input v-model="email.linkParams" class="console-input mono">
              </div>
            </div>
            <div>
              <div class="console-label" style="margin-bottom:7px;">Campaign web ID</div>
              <input v-model="email.web_id" class="console-input mono">
            </div>
          </div>

          <!-- ===== Sections ===== -->
          <div v-show="activeTab === 'sections'">
            <!-- body mode: structured builder vs full raw HTML -->
            <div class="mb-3 flex items-center justify-between">
              <ConsoleSegmentedToggle
                :model-value="bodyMode"
                :options="[{ label: 'Builder', value: 'builder' }, { label: 'Raw HTML', value: 'html' }]"
                size="sm"
                @update:model-value="onBodyModeChange"
              />
              <span v-if="email.htmlMode" style="color:var(--muted); font-size:13px;">Raw HTML replaces the section layout.</span>
            </div>

            <!-- ===== Raw HTML mode ===== -->
            <div v-if="email.htmlMode">
              <ConsoleCodeEditor v-model="email.rawHtml" lang="html" height="440px" />
                  <div class="mt-3 flex flex-wrap items-center gap-2">
                    <span style="color:var(--muted); font-size:13px;">Insert an Outlook snippet</span>
                    <button v-for="s in outlookSnippets" :key="s.label" type="button" class="console-dashed" :title="s.title" @click="insertRawSnippet(s.code)">{{ s.label }}</button>
                  </div>
                  <ul v-if="rawLint.length" class="lint">
                    <li v-for="(issue, li) in rawLint" :key="li" :class="{ warn: issue.level === 'warn' }">{{ issue.message }}</li>
                  </ul>
            </div>

            <!-- ===== Structured builder mode ===== -->
            <template v-else>
            <!-- layout bar: separate desktop / mobile trees -->
            <div class="mb-3 flex items-center gap-2.5" style="flex-wrap:wrap;">
              <template v-if="separateLayout">
                <ConsoleSegmentedToggle
                  :model-value="editingLayout"
                  :options="[{ label: 'Desktop', value: 'desktop' }, { label: 'Mobile', value: 'mobile' }]"
                  size="sm"
                  @update:model-value="(v) => (editingLayout = v as 'desktop' | 'mobile')"
                />
                <AppButton
                  small
                  :title="`Replace the ${editingLayout} layout with a copy of the ${editingLayout === 'desktop' ? 'mobile' : 'desktop'} layout`"
                  @click="copyLayout(editingLayout === 'desktop' ? 'mobile' : 'desktop')"
                >Copy from {{ editingLayout === 'desktop' ? 'mobile' : 'desktop' }}</AppButton>
                <div style="flex:1;" />
                <AppButton small variant="danger" @click="disableSeparateMobileLayout">Remove mobile layout</AppButton>
              </template>
              <template v-else>
                <span style="color:var(--muted);">One layout for desktop and mobile. Each image swaps by size.</span>
                <div style="flex:1;" />
                <AppButton small title="Give mobile its own section layout, independent of desktop" @click="enableSeparateMobileLayout">Separate mobile layout</AppButton>
              </template>
            </div>

            <!-- slice status + CTA -->
            <div v-if="slicesLoaded" class="notice mb-3">
              <div style="flex:1; color:var(--muted);">
                Slices:
                <span :style="{ color: sliceSizeCounts.desktop ? 'var(--good)' : 'var(--faint)' }">desktop {{ sliceSizeCounts.desktop || 'none' }}</span>,
                <span :style="{ color: sliceSizeCounts.mobile ? 'var(--good)' : 'var(--faint)' }">mobile {{ sliceSizeCounts.mobile || 'none' }}</span>
              </div>
              <AppButton small @click="goSlice">
                <UIcon name="i-heroicons-scissors" class="h-4 w-4" />
                Slice images
              </AppButton>
            </div>

            <!-- missing-size warning: a size the built email needs has no slices → that
                 preview/inbox shows broken images. Drive the user to slice its design comp. -->
            <div
              v-for="m in missingSizes"
              :key="m.size"
              class="notice mb-3"
              style="border-color:color-mix(in srgb, var(--warn) 35%, transparent); background:color-mix(in srgb, var(--warn) 8%, var(--panel));"
            >
              <UIcon name="i-heroicons-exclamation-triangle" class="h-5 w-5" style="color:var(--warn); flex-shrink:0;" />
              <div style="flex:1;">
                <div style="font-weight:600; color:var(--warn);">No {{ m.size }} slices. The {{ m.size }} view will show broken images.</div>
                <div style="color:var(--muted);">
                  {{ m.hasComp
                    ? `Slice the ${m.size} design to generate its images.`
                    : `No ${m.size} design is attached. Add one to this email’s source assets, then slice it.` }}
                </div>
              </div>
              <AppButton v-if="m.hasComp" small @click="goSlice">Slice {{ m.size }}</AppButton>
            </div>

            <!-- sync banner -->
            <div
              v-if="slicesLoaded && missingSlices.length > 0"
              class="notice mb-3.5"
              style="border-color:color-mix(in srgb, var(--cyan) 30%, transparent); background:color-mix(in srgb, var(--cyan) 8%, var(--panel));"
            >
              <UIcon name="i-heroicons-sparkles" class="h-5 w-5" style="color:var(--cyan); flex-shrink:0;" />
              <div style="flex:1;">
                <div style="font-weight:600; color:var(--cyan);">{{ missingSlices.length }} new cell{{ missingSlices.length === 1 ? '' : 's' }} found in storage</div>
                <div class="mono" style="color:var(--muted);">Slices {{ missingSlices.map(n => String(n).padStart(2, '0')).join(', ') }} are available and not placed yet.</div>
              </div>
              <AppButton small variant="primary" :disabled="syncingSlices" @click="syncSlices">Sync</AppButton>
            </div>

            <!-- summary, filter and add-section bar -->
            <div class="mb-3 flex items-center justify-between gap-3" style="flex-wrap:wrap;">
              <div class="flex items-center gap-3" style="flex-wrap:wrap;">
                <span class="mono">{{ separateLayout ? editingLayout + ': ' : '' }}{{ sectionSummary }}</span>
                <span v-if="totalIssues.count" style="display:inline-flex; align-items:center; gap:6px; color:var(--warn); font-weight:600;">
                  <UIcon name="i-heroicons-exclamation-triangle" class="h-4 w-4" />
                  {{ issueText(totalIssues) }}
                </span>
                <AppButton
                  v-if="totalIssues.count || onlyIncomplete"
                  small
                  :variant="onlyIncomplete ? 'primary' : 'quiet'"
                  :aria-pressed="onlyIncomplete"
                  @click="onlyIncomplete = !onlyIncomplete"
                >{{ onlyIncomplete ? 'Showing incomplete only' : 'Show incomplete only' }}</AppButton>
              </div>
              <div class="flex items-center gap-1.5" style="flex-wrap:wrap;">
                <span style="color:var(--muted);">Add</span>
                <button class="console-addbtn" @click="addTextSection">Text</button>
                <button class="console-addbtn" @click="addImageBand">Image band</button>
                <button class="console-addbtn" @click="addButton">Button</button>
                <button class="console-addbtn" @click="addDivider">Divider</button>
                <button class="console-addbtn" @click="addSpacer">Spacer</button>
                <button class="console-addbtn" @click="addHtmlSection">HTML</button>
              </div>
            </div>

            <!-- section cards -->
            <div class="flex flex-col gap-2.5">
              <div
                v-for="(section, sectionIndex) in activeSections"
                v-show="!onlyIncomplete || sectionIssues(section).count"
                :key="sectionIndex"
                class="sec-card"
                :class="{ open: openIndex === sectionIndex }"
                :style="dragIndex === sectionIndex ? 'opacity:0.4;' : ''"
                draggable="true"
                @dragstart="onDragStart(sectionIndex)"
                @dragover.prevent
                @drop="onDrop(sectionIndex); openIndex = null"
              >
                <!-- Collapsed row: type, title, cell thumbnails, what is missing. One section open at a time. -->
                <div class="sec-head">
                  <span class="sec-grip" title="Drag to reorder"><UIcon name="i-heroicons-bars-2" class="h-4 w-4" /></span>
                  <button
                    type="button"
                    class="sec-toggle"
                    :aria-expanded="openIndex === sectionIndex"
                    @click="openIndex = openIndex === sectionIndex ? null : sectionIndex"
                  >
                    <span class="sec-type" :style="chipStyle(section)">{{ sectionTypeLabel(section) }}</span>
                    <span class="truncate" style="flex:1 1 120px; min-width:0; font-weight:600;">{{ sectionTitle(section) }}</span>
                    <span v-if="section.type === 'image'" class="sec-thumbs">
                      <span
                        v-for="(cell, ci) in (section.cells || []).slice(0, 8)"
                        :key="ci"
                        class="sec-thumb"
                        :class="{ miss: !cell.link || !cell.alt }"
                      >
                        <img v-if="cell.src" :src="thumbUrl(cell.src)" alt="" loading="lazy">
                      </span>
                    </span>
                    <span class="sec-status mono" :style="{ color: sectionIssues(section).count ? 'var(--warn)' : 'var(--muted)' }">{{ sectionStatus(section) }}</span>
                    <UIcon
                      name="i-heroicons-chevron-down"
                      class="h-4 w-4"
                      style="color:var(--muted); flex-shrink:0; transition:transform 0.12s ease;"
                      :style="{ transform: openIndex === sectionIndex ? 'rotate(180deg)' : 'none' }"
                    />
                  </button>
                  <AppButton
                    variant="ghost"
                    icon
                    small
                    aria-label="Remove section"
                    title="Remove section"
                    @click="removeSection(sectionIndex); openIndex = null"
                  ><UIcon name="i-heroicons-x-mark" class="h-4 w-4" /></AppButton>
                </div>

                <template v-if="openIndex === sectionIndex">
                <!-- text body -->
                <div v-if="section.type === 'text'" class="sec-body">
                  <label class="field">
                    <span>Heading</span>
                    <input v-model="section.heading" class="console-input" style="font-weight:600;">
                  </label>
                  <label class="field">
                    <span>Body text</span>
                    <textarea v-model="section.body" rows="3" class="console-input" style="resize:vertical;" />
                  </label>
                  <div class="flex flex-wrap items-center gap-2">
                    <button type="button" class="console-fmt" style="font-weight:700;" :class="{ on: section.style?.bold }" :aria-pressed="!!section.style?.bold" @click="toggleStyle(section, 'bold')">Bold</button>
                    <button type="button" class="console-fmt" style="font-style:italic;" :class="{ on: section.style?.italic }" :aria-pressed="!!section.style?.italic" @click="toggleStyle(section, 'italic')">Italic</button>
                    <button type="button" class="console-fmt" title="Change alignment" @click="cycleAlign(section)">Align {{ section.style?.align || 'center' }}</button>
                    <label class="console-fmt">
                      Text colour
                      <input type="color" class="swatch" :value="section.style?.color || '#000000'" @input="setColor(section, $event)">
                    </label>
                  </div>
                </div>

                <!-- image band body -->
                <div v-else-if="section.type === 'image'" class="cells">
                  <div v-for="(cell, cellIndex) in section.cells" :key="cellIndex" class="cell">
                    <div class="cell-media">
                      <!-- thumbnail; hovering shows a larger preview panel (teleported so the
                           card's overflow:hidden can't clip it) -->
                      <span
                        class="cell-thumb__box"
                        @mouseenter="cell.src && showThumbPreview(cell.src, $event)"
                        @mouseleave="hideThumbPreview"
                      >
                        <img v-if="cell.src" :src="thumbUrl(cell.src)" :alt="cell.src" style="width:100%; height:100%; object-fit:cover;" loading="lazy" @load="onThumbLoad" @error="onThumbError">
                      </span>
                      <input
                        v-model="cell.src"
                        class="console-input mono"
                        aria-label="Image file"
                        placeholder="$SIZE_01.jpg"
                        style="height:32px; font-size:13px;"
                      >
                    </div>

                    <div class="cell-fields">
                      <label class="cell-field">
                        <span class="cell-field-head"><span>Link</span><span v-if="!cell.link" class="cell-miss">Missing</span></span>
                        <input
                          v-model="cell.link"
                          :data-link-input="`${sectionIndex}-${cellIndex}`"
                          type="url"
                          placeholder="https://"
                          class="console-input"
                          :class="{ miss: !cell.link }"
                        >
                      </label>
                      <!-- alt text: read out by screen readers and shown when images are blocked. Auto-filled by the
                           slicer's OCR; editable here. Auto-growing textarea since OCR'd copy can run several lines. -->
                      <div class="cell-field">
                        <span class="cell-field-head"><span>Alt text</span><span v-if="!cell.alt" class="cell-miss">Missing</span></span>
                        <div class="flex gap-2" style="align-items:flex-start; flex-wrap:wrap;">
                          <ConsoleAutoTextarea
                            v-model="cell.alt"
                            aria-label="Alt text"
                            placeholder="Describe the image for screen readers and image-blocked inboxes"
                            class="console-input"
                            :class="{ miss: !cell.alt }"
                            style="flex:1 1 220px; min-width:0;"
                          />
                          <AppButton
                            small
                            style="height:38px;"
                            :disabled="!cell.src || ocrBusy !== null"
                            :title="cell.src ? 'Read the words in this image again and replace the alt text' : 'Set an image file first'"
                            @click="rerunCellOcr(cell, `${sectionIndex}-${cellIndex}`)"
                          >{{ ocrBusy === `${sectionIndex}-${cellIndex}` ? 'Reading…' : 'Read text from image' }}</AppButton>
                        </div>
                      </div>
                    </div>

                    <AppButton
                      v-if="section.cells && section.cells.length > 1"
                      variant="ghost"
                      icon
                      small
                      aria-label="Remove cell"
                      title="Remove cell"
                      @click="removeCell(section, cellIndex)"
                    ><UIcon name="i-heroicons-x-mark" class="h-4 w-4" /></AppButton>
                  </div>
                  <div class="flex gap-1.5" style="padding:12px 16px;">
                    <button class="console-dashed" @click="splitIntoCells(section)">Split into cells</button>
                    <button class="console-dashed" @click="addCell(section)">Add cell</button>
                  </div>
                </div>

                <!-- spacer body -->
                <div v-else-if="section.type === 'spacer'" class="sec-body">
                  <label class="field" style="max-width:160px;">
                    <span>Height in pixels</span>
                    <input v-model.number="section.height" type="number" min="0" class="console-input mono">
                  </label>
                </div>

                <!-- button / CTA body -->
                <div v-else-if="section.type === 'button'" class="sec-body">
                  <div class="field-grid">
                    <label class="field">
                      <span>Button label</span>
                      <input v-model="section.buttonText" class="console-input" style="font-weight:600;">
                    </label>
                    <label class="field">
                      <span>Link</span>
                      <input v-model="section.buttonUrl" type="url" placeholder="https://" class="console-input">
                    </label>
                  </div>
                  <div class="flex flex-wrap items-center gap-2">
                    <button type="button" class="console-fmt" title="Change alignment" @click="cycleButtonAlign(section)">Align {{ section.buttonStyle?.align || 'center' }}</button>
                    <label class="console-fmt">
                      Fill
                      <input type="color" class="swatch" :value="section.buttonStyle?.background || '#151e6d'" @input="setButtonStyle(section, 'background', ($event.target as HTMLInputElement).value)">
                    </label>
                    <label class="console-fmt">
                      Text colour
                      <input type="color" class="swatch" :value="section.buttonStyle?.color || '#ffffff'" @input="setButtonStyle(section, 'color', ($event.target as HTMLInputElement).value)">
                    </label>
                    <label class="console-fmt">
                      Corner radius
                      <input type="number" min="0" max="40" class="mini mono" :value="section.buttonStyle?.radius ?? 6" @input="setButtonStyle(section, 'radius', Number(($event.target as HTMLInputElement).value))">
                    </label>
                  </div>
                </div>

                <!-- divider body -->
                <div v-else-if="section.type === 'divider'" class="sec-body">
                  <div class="flex flex-wrap items-center gap-2">
                    <label class="console-fmt">
                      Colour
                      <input type="color" class="swatch" :value="section.dividerColor || '#dddddd'" @input="section.dividerColor = ($event.target as HTMLInputElement).value">
                    </label>
                    <label class="console-fmt">
                      Thickness in pixels
                      <input v-model.number="section.dividerThickness" type="number" min="1" max="12" class="mini mono">
                    </label>
                  </div>
                </div>

                <!-- html body -->
                <div v-else-if="section.type === 'html'" class="sec-body">
                  <ConsoleCodeEditor v-model="section.html" lang="html" height="240px" />
                  <div class="mt-3 flex flex-wrap items-center gap-2">
                    <span style="color:var(--muted); font-size:13px;">Insert an Outlook snippet</span>
                    <button v-for="s in outlookSnippets" :key="s.label" type="button" class="console-dashed" :title="s.title" @click="insertSnippetInto(section, s.code)">{{ s.label }}</button>
                  </div>
                  <ul v-if="lintFor(section.html).length" class="lint">
                    <li v-for="(issue, li) in lintFor(section.html)" :key="li" :class="{ warn: issue.level === 'warn' }">{{ issue.message }}</li>
                  </ul>
                </div>
                </template>
              </div>

              <div v-if="activeSections.length === 0" class="sec-empty">
                No {{ separateLayout ? editingLayout + ' ' : '' }}sections yet. Add one above{{ separateLayout ? `, or copy the ${editingLayout === 'desktop' ? 'mobile' : 'desktop'} layout` : '' }}.
              </div>
            </div>
            </template>
          </div>

          <!-- ===== Variables ===== -->
          <div v-show="activeTab === 'details'" style="margin-top:24px; padding-top:20px; border-top:1px solid var(--line);">
            <h2 style="margin:0 0 8px; font-size:20px; font-weight:700;">Template variables</h2>
            <p style="color:var(--muted); margin:0 0 14px;">
              Values set here override the workspace and template defaults for this email.
              Available: footerBgColor, headerBgColor, bodyBgColor, textColor, linkColor, footerTextColor.
            </p>
            <div v-if="!email.vars || Object.keys(email.vars).length === 0">
              <button class="console-addbtn" @click="addVar">Add variables</button>
            </div>
            <div v-else class="flex flex-col gap-2">
              <div v-for="(value, key) in email.vars" :key="key" class="flex gap-2">
                <input :value="key" readonly class="console-input mono" style="flex:1;">
                <input v-model="email.vars[key]" placeholder="Value" class="console-input" style="flex:1;">
                <AppButton icon variant="ghost" :aria-label="`Remove ${key}`" @click="deleteVar(key)">
                  <UIcon name="i-heroicons-x-mark" class="h-4 w-4" />
                </AppButton>
              </div>
              <button class="console-addbtn" style="align-self:flex-start; margin-top:4px;" @click="addVar">Add variable</button>
            </div>
          </div>
        </div>
      </div>

      <!-- right: source assets and live preview -->
      <div class="editor-side">
        <!-- source assets (copy doc + design comps pushed in from the portal) -->
        <div class="mb-4">
          <EmailAssetsPanel :assets="email.assets" />
        </div>

        <h2 style="margin:0 0 4px; font-size:20px; font-weight:700;">Live preview</h2>
        <div id="email-preview">
          <EmailPreview :key="previewKey" :group="group" :email="email" @edit-link="focusLinkInput" />
        </div>
      </div>
    </div>

    <!-- Larger slice preview, shown while hovering a cell thumbnail. Teleported + fixed so
         the sections list's overflow:hidden can't clip it. -->
    <Teleport to="body">
      <div
        v-if="thumbPreview"
        class="cell-thumb-preview"
        :style="{ left: thumbPreview.left + 'px', top: thumbPreview.top + 'px' }"
      >
        <img :src="thumbPreview.src" alt="" loading="lazy">
      </div>
    </Teleport>
  </div>
</template>

<script setup lang="ts">
import { until, watchDebounced } from "@vueuse/core"
import { onBeforeRouteLeave } from "vue-router"
import ConsoleCodeEditor from "~/components/Console/CodeEditor.vue"
import { normalizeSections, serializeSections, createTextSection, createImageSection, createSpacerSection, createButtonSection, createDividerSection, createHtmlSection, isRawHtmlEmail, rawHtmlFromSections, rawHtmlToSections } from "~/composables/useEmailSections"
import { outlookSnippets } from "~/composables/useOutlookSnippets"
import { lintEmailHtml } from "~/composables/useEmailLint"

const orgs = useOrganizationsStore()
const orgOptions = computed(() => orgs.items.map(org => ({ label: org.name, value: org.alias })))
const route = useRoute()
const { groupAlias, emailAlias } = route.params as { groupAlias: string, emailAlias: string }
const emailsStore = useEmailsStore()
const organizationsStore = useOrganizationsStore()
const group = computed(() => organizationsStore.map[groupAlias])
const email = computed({
  get() {
    if (!group.value) return null
    if (!emailsStore.map) return null
    return emailsStore.map[emailAlias] || null
  },
  set(value) {
    if (value && emailsStore.map) {
      emailsStore.map[emailAlias] = value
    }
  }
})

// Force preview re-render
const previewKey = ref(0)
const saving = ref(false)
const dirty = ref(false)
let savedSnapshot = ''

// ---- Separate mobile layout ----
// When `mobileSections` is an array, the email has a separate mobile layout: `sections` is
// desktop, `mobileSections` is mobile. `editingLayout` picks which tree the Sections editor
// (and its add/remove/reorder) operates on. All the section functions target `activeSections`.
const editingLayout = ref<'desktop' | 'mobile'>('desktop')
const separateLayout = computed(() => Array.isArray(email.value?.mobileSections))
const activeSections = computed<EmailSectionT[]>({
  get() {
    if (!email.value) return []
    return separateLayout.value && editingLayout.value === 'mobile'
      ? email.value.mobileSections!
      : email.value.sections
  },
  set(value) {
    if (!email.value) return
    if (separateLayout.value && editingLayout.value === 'mobile') email.value.mobileSections = value
    else email.value.sections = value
  },
})

function enableSeparateMobileLayout() {
  if (!email.value || separateLayout.value) return
  email.value.mobileSections = [] // starts empty; use "Copy from desktop" to seed
  editingLayout.value = 'mobile'
}
async function disableSeparateMobileLayout() {
  if (!email.value || !separateLayout.value) return
  const ok = await useConfirmDialog().confirm({
    title: 'Remove the separate mobile layout?',
    message: 'The mobile layout and its sections will be discarded; mobile will fall back to the desktop layout. This can’t be undone.',
    action: 'Remove mobile layout',
  })
  if (!ok) return
  email.value.mobileSections = null as unknown as EmailSectionT[] // null clears the column on save
  editingLayout.value = 'desktop'
}
// Deep-clone one tree into the other so you can start from the existing layout and diverge.
function copyLayout(from: 'desktop' | 'mobile') {
  if (!email.value || !separateLayout.value) return
  const src = from === 'desktop' ? email.value.sections : (email.value.mobileSections || [])
  const clone = JSON.parse(JSON.stringify(normalizeSections(src))) as EmailSectionT[]
  if (from === 'desktop') email.value.mobileSections = clone
  else email.value.sections = clone
}

// Normalize legacy sections to the typed shape on load (lazy migration).
onMounted(() => {
  if (email.value) {
    // Decode persisted raw-HTML mode (a lone `raw` html section) into the editor flags.
    if (isRawHtmlEmail(email.value.sections)) {
      email.value.htmlMode = true
      email.value.rawHtml = rawHtmlFromSections(email.value.sections)
    }
    email.value.sections = normalizeSections(email.value.sections)
    if (Array.isArray(email.value.mobileSections)) {
      email.value.mobileSections = normalizeSections(email.value.mobileSections)
    }
    savedSnapshot = JSON.stringify(email.value)
  }
})

// ---- Unsaved-changes guard ----
// Browser-level (refresh / tab close / external link): native prompt while dirty.
function beforeUnload(e: BeforeUnloadEvent) {
  if (!dirty.value) return
  e.preventDefault()
  e.returnValue = "" // Chrome requires a set returnValue to show the prompt
}
onMounted(() => window.addEventListener("beforeunload", beforeUnload))
onBeforeUnmount(() => window.removeEventListener("beforeunload", beforeUnload))

// In-app navigation: confirm before leaving with unsaved edits.
onBeforeRouteLeave(async () => {
  if (!dirty.value) return true
  return await useConfirmDialog().confirm({
    title: "Discard unsaved changes?",
    message: "This email has unsaved changes. Leave the page without saving them?",
    action: "Discard changes",
  })
})

// The tab is the email page's route param. This component renders two of its tabs:
// "sections" and "details".
const activeTab = useRouteTab('tab', 'preview')
function sectionTypeLabel(section: EmailSectionT): string {
  if (section.type === 'text') return 'TEXT'
  if (section.type === 'image') return 'IMAGE'
  if (section.type === 'button') return 'BUTTON'
  if (section.type === 'divider') return 'DIVIDER'
  if (section.type === 'html') return 'HTML'
  return 'SPACER'
}

function chipStyle(section: EmailSectionT) {
  const map = { text: '--violet', image: '--acctext', spacer: '--muted', button: '--cyan', divider: '--faint', html: '--warn' } as const
  return { color: `var(${map[(section.type as keyof typeof map) || 'image']})` }
}

function sectionStatus(section: EmailSectionT): string {
  if (section.type !== 'image') return sectionMeta(section)
  return issueText(sectionIssues(section)) || 'Complete'
}

const totalIssues = computed(() => totalSectionIssues(activeSections.value))

const sectionSummary = computed(() => {
  const sections = activeSections.value.length
  const cells = activeSections.value.reduce((n, s) => n + (s.type === 'image' ? s.cells?.length ?? 0 : 0), 0)
  return `${sections} ${sections === 1 ? 'section' : 'sections'}, ${cells} ${cells === 1 ? 'cell' : 'cells'}`
})

const onlyIncomplete = ref(false)

// One section is open at a time; the rest stay as one-line rows. Tracked by index,
// so it survives a save. Reordering or removing a section closes it.
const openIndex = ref<number | null>(null)
watch(() => activeSections.value.length, (n, old) => {
  // A section was just added at the end: open it for editing.
  if (n === old + 1) openIndex.value = n - 1
})
watch(editingLayout, () => { openIndex.value = null })

function sectionTitle(section: EmailSectionT): string {
  if (section.type === 'text') return section.heading || 'Text block'
  if (section.type === 'spacer') return 'Spacer'
  if (section.type === 'button') return section.buttonText || 'Button'
  if (section.type === 'divider') return 'Divider'
  if (section.type === 'html') return 'HTML block'
  return 'Image band'
}

function sectionMeta(section: EmailSectionT): string {
  if (section.type === 'text') return 'heading + body'
  if (section.type === 'spacer') return `${section.height ?? 24}px`
  if (section.type === 'button') return section.buttonUrl ? 'linked' : 'no link'
  if (section.type === 'divider') return `${section.dividerThickness ?? 1}px`
  if (section.type === 'html') return `${(section.html || '').length} chars`
  const n = section.cells?.length ?? 0
  return `${n} cell${n === 1 ? '' : 's'}${n > 1 ? ' · split' : ''}`
}

// Append an Outlook/MSO snippet to a section's HTML (v-model pushes it into the editor).
function insertSnippetInto(section: EmailSectionT, code: string) {
  section.html = `${section.html || ''}${section.html && !section.html.endsWith('\n') ? '\n' : ''}${code}`
}

// Email-CSS lint for a chunk of author HTML (cached-ish via a plain call; content is small).
function lintFor(html?: string) {
  return lintEmailHtml(html || '')
}

function addTextSection() {
  activeSections.value.push(createTextSection())
}
function addImageBand() {
  activeSections.value.push(createImageSection())
}
function addSpacer() {
  activeSections.value.push(createSpacerSection())
}
function addButton() {
  activeSections.value.push(createButtonSection())
}
function addDivider() {
  activeSections.value.push(createDividerSection())
}
function addHtmlSection() {
  activeSections.value.push(createHtmlSection())
}

// ---- Raw-HTML body mode ----
// bodyMode toggles email.htmlMode. Switching to raw the first time seeds rawHtml from the
// current built HTML so authors can start from what the section builder produced.
const bodyMode = computed<'builder' | 'html'>({
  get: () => (email.value?.htmlMode ? 'html' : 'builder'),
  set: (mode) => {
    if (!email.value) return
    if (mode === 'html') {
      // rawHtml is the BODY content (the wrapper is applied around it on build), so seed
      // with a content starter rather than the full built document.
      if (!email.value.rawHtml) email.value.rawHtml = defaultRawHtml()
      email.value.htmlMode = true
    } else {
      email.value.htmlMode = false
    }
  },
})

async function onBodyModeChange(value: string | number) {
  const mode: 'builder' | 'html' = value === 'html' ? 'html' : 'builder'
  if (mode === bodyMode.value) return
  // Warn before replacing an existing structured layout with raw HTML.
  if (mode === 'html' && !email.value?.htmlMode && (email.value?.sections?.length || 0) > 0 && !email.value?.rawHtml) {
    const ok = await useConfirmDialog().confirm({
      title: 'Switch to raw HTML?',
      danger: false,
      message: 'Raw HTML mode replaces the section layout for this email when you save. You can switch back, but the section blocks won’t be regenerated.',
      action: 'Switch',
    })
    if (!ok) return
  }
  bodyMode.value = mode
}

function defaultRawHtml() {
  return `<table role="presentation" width="100%" cellpadding="0" cellspacing="0" style="border-collapse:collapse;">
  <tr>
    <td align="center" style="padding:24px;font-family:Arial,sans-serif;">
      <h1 style="margin:0 0 8px;font-size:22px;color:#151e6d;">Hello</h1>
      <p style="margin:0;font-size:15px;color:#333;line-height:1.5;">Write your email in raw HTML here.</p>
    </td>
  </tr>
</table>`
}

const rawLint = computed(() => lintEmailHtml(email.value?.rawHtml || ''))

function insertRawSnippet(code: string) {
  if (!email.value) return
  email.value.rawHtml = `${email.value.rawHtml || ''}${email.value.rawHtml && !email.value.rawHtml.endsWith('\n') ? '\n' : ''}${code}`
}

function cycleButtonAlign(section: EmailSectionT) {
  const order = ['left', 'center', 'right'] as const
  if (!section.buttonStyle) section.buttonStyle = {}
  const cur = section.buttonStyle.align || 'center'
  section.buttonStyle.align = order[(order.indexOf(cur) + 1) % order.length]
}
function setButtonStyle(section: EmailSectionT, key: 'background' | 'color' | 'radius', value: string | number) {
  if (!section.buttonStyle) section.buttonStyle = {}
  ;(section.buttonStyle as any)[key] = value
}
function removeSection(index: number) {
  activeSections.value.splice(index, 1)
}

function addCell(section: EmailSectionT) {
  if (!section.cells) section.cells = []
  section.cells.push({ src: '$SIZE_01.jpg', link: '' })
}
function splitIntoCells(section: EmailSectionT) {
  // Splitting a single-cell band into two side-by-side cells (vertical split).
  addCell(section)
}
function removeCell(section: EmailSectionT, index: number) {
  section.cells?.splice(index, 1)
}

// Text style toolbar
function ensureStyle(section: EmailSectionT): TextStyleT {
  if (!section.style) section.style = {}
  return section.style
}
function toggleStyle(section: EmailSectionT, key: 'bold' | 'italic') {
  const s = ensureStyle(section)
  s[key] = !s[key]
}
function cycleAlign(section: EmailSectionT) {
  const s = ensureStyle(section)
  const order: Array<'left' | 'center' | 'right'> = ['left', 'center', 'right']
  const idx = order.indexOf(s.align || 'center')
  s.align = order[(idx + 1) % order.length]
}
function setColor(section: EmailSectionT, event: Event) {
  ensureStyle(section).color = (event.target as HTMLInputElement).value
}
// ---- Drag reorder ----
const dragIndex = ref<number | null>(null)
function onDragStart(index: number) {
  dragIndex.value = index
}
function onDrop(index: number) {
  if (dragIndex.value === null || dragIndex.value === index) {
    dragIndex.value = null
    return
  }
  const arr = activeSections.value
  const [moved] = arr.splice(dragIndex.value, 1)
  arr.splice(index, 0, moved!)
  dragIndex.value = null
}

// ---- Thumbnails ----
const { getImageUrl } = useImageUrl()
function sliceFilename(src: string): string {
  // $SIZE slices may exist for only one size (e.g. a mobile-only email), so interpolate to
  // whichever size actually has files — preferring desktop. A hardcoded "desktop" 404s for
  // mobile-only emails, leaving the raw "$SIZE" alt text showing over a broken thumbnail.
  const size = sliceSizeCounts.value.desktop ? 'desktop' : sliceSizeCounts.value.mobile ? 'mobile' : 'desktop'
  return src.replace('$SIZE', size)
}
function thumbUrl(src: string): string {
  if (!src) return ''
  const filename = sliceFilename(src)
  const url = getImageUrl(groupAlias, emailAlias, filename)
  // Re-slicing overwrites each slice under the same filename, and the CDN serves them with
  // `max-age=14400`, so a bare URL shows the PREVIOUS slice for up to 4h — the thumbnails
  // silently disagreed with the (cache-busted) main preview. Key on the file's mtime: stable
  // across re-renders, so it doesn't re-download on every keystroke the way `Date.now()` would.
  const v = sliceVersion(filename)
  return v ? `${url}${url.includes('?') ? '&' : '?'}v=${v}` : url
}
function onThumbLoad(event: Event) {
  // Clear any dim left by a prior error on this <img> — the src is reactive, so a thumbnail
  // that first 404'd (e.g. desktop before slices loaded) and then resolved would otherwise
  // stay at 0.2 opacity, letting the striped placeholder show through the loaded image.
  ;(event.target as HTMLImageElement).style.opacity = '1'
}
function onThumbError(event: Event) {
  const img = event.target as HTMLImageElement
  img.style.opacity = '0.2'
}

// Hover preview: a fixed, viewport-positioned panel anchored to the right of the hovered
// thumbnail. Kept in page state (not CSS :hover) because it's teleported out to escape the
// sections list's overflow:hidden — CSS alone can't reference the thumbnail's position.
const thumbPreview = ref<{ src: string; left: number; top: number } | null>(null)
function showThumbPreview(src: string, event: MouseEvent) {
  const rect = (event.currentTarget as HTMLElement).getBoundingClientRect()
  // Clamp vertically so a thumbnail near the top/bottom edge doesn't push the panel off-screen.
  const top = Math.min(Math.max(rect.top + rect.height / 2, 90), window.innerHeight - 90)
  thumbPreview.value = { src: thumbUrl(src), left: rect.right + 8, top }
}
function hideThumbPreview() {
  thumbPreview.value = null
}

// ---- Per-image OCR ----
// Re-run OCR on a single slice to (re)generate its alt text. The slice is fetched through a
// same-origin proxy (raw.get.ts) so it can be drawn onto a canvas without tainting it — the
// CDN images are cross-origin. Reuses the slicer's OCR (grayscale/normalize + tesseract).
const { recognize: ocrRecognize, terminate: ocrTerminate } = useOcr()
const ocrBusy = ref<string | null>(null) // `${sectionIndex}-${cellIndex}` currently running

function sliceRawUrl(src: string): string {
  const file = sliceFilename(src)
  // Same cache key as the thumbnails — without it, re-running OCR after a re-slice reads the
  // stale image off the CDN and writes alt text describing the previous slice.
  const v = sliceVersion(file)
  const query = `file=${encodeURIComponent(file)}${v ? `&v=${v}` : ''}`
  return `/api/v1/images/${groupAlias}/${emailAlias}/raw?${query}`
}

async function rerunCellOcr(cell: EmailImageT, key: string) {
  if (!cell.src || ocrBusy.value) return
  ocrBusy.value = key
  try {
    const blob = await $fetch<Blob>(sliceRawUrl(cell.src), { responseType: 'blob' })
    const bitmap = await createImageBitmap(blob)
    const canvas = document.createElement('canvas')
    canvas.width = bitmap.width
    canvas.height = bitmap.height
    const ctx = canvas.getContext('2d')
    if (!ctx) throw new Error('no canvas context')
    ctx.drawImage(bitmap, 0, 0)
    bitmap.close?.()
    const text = await ocrRecognize(canvas)
    if (text) {
      cell.alt = text
      useToast().add({ title: 'Alt text updated', description: 'Re-ran OCR for this image.', color: 'success' })
    } else {
      useToast().add({ title: 'No text found', description: 'OCR read no text from this image.', color: 'neutral' })
    }
  } catch (error) {
    console.error('Re-run OCR failed:', error)
    useToast().add({ title: 'OCR failed', description: 'Could not re-run OCR for this image.', color: 'error' })
  } finally {
    ocrBusy.value = null
    ocrTerminate() // free the worker + WASM until next use
  }
}

// ---- Slice auto-detection ----
const {
  availableSlices,
  sizeCounts: sliceSizeCounts,
  sliceVersion,
  loading: slicesLoading,
  loaded: slicesLoaded,
  fetchSlices,
  missingSliceNumbers,
  applyMissing,
} = useEmailSlices(groupAlias, emailAlias)
const syncingSlices = ref(false)
const missingSlices = computed(() => missingSliceNumbers(email.value))
void availableSlices
void slicesLoading

// The builder emits BOTH a desktop_* and mobile_* image for every $SIZE slice, so if the
// email references slices but a size has zero files in storage, that size renders broken
// (the blue placeholder bars). Detect those sizes and drive the user to slice the matching
// design comp.
const usesSlices = computed(() =>
  normalizeSections(email.value?.sections).some(
    (s) => s.type === 'image' && (s.cells || []).some((c) => /\$SIZE|(?:desktop|mobile)_\d/i.test(c.src || '')),
  ),
)
function hasDesignComp(role: 'design_desktop' | 'design_mobile') {
  return (email.value?.assets || []).some((a) => a.role === role)
}
const missingSizes = computed(() => {
  if (!slicesLoaded.value || !usesSlices.value) return []
  const out: Array<{ size: 'desktop' | 'mobile'; hasComp: boolean }> = []
  if (!sliceSizeCounts.value.desktop) out.push({ size: 'desktop', hasComp: hasDesignComp('design_desktop') })
  if (!sliceSizeCounts.value.mobile) out.push({ size: 'mobile', hasComp: hasDesignComp('design_mobile') })
  return out
})

function goSlice() {
  navigateTo(`/app/emails/${groupAlias}/${emailAlias}/slice`)
}

async function syncSlices() {
  if (!email.value || syncingSlices.value) return
  syncingSlices.value = true
  try {
    const { added } = applyMissing(email.value)
    if (added.length === 0) {
      useToast().add({ title: "Nothing to sync", description: "All available slices already have entries.", color: "neutral" })
      return
    }
    await saveEmail()
    useToast().add({
      title: "Slices synced",
      description: `Added ${added.length} cell${added.length === 1 ? '' : 's'} for missing slice${added.length === 1 ? '' : 's'}.`,
      color: "success",
    })
  } finally {
    syncingSlices.value = false
  }
}

watch(activeTab, (tab) => {
  if (tab === 'sections' && !slicesLoaded.value && !slicesLoading.value) {
    fetchSlices()
  }
}, { immediate: true })

// Live-reload preview + dirty tracking on email edits (debounced)
watchDebounced(
  () => email.value && JSON.stringify(email.value),
  (json) => {
    previewKey.value++
    if (savedSnapshot) dirty.value = json !== savedSnapshot
  },
  { debounce: 300, maxWait: 1000 },
)

// ---- Variables ----
async function addVar() {
  if (!email.value) return
  if (!email.value.vars) email.value.vars = {}

  const wrapperAlias = email.value.wrapper || group.value?.wrapper || 'default'
  const { parts } = await useEmailParts()
  const wrapper = parts.value.find(part => part.alias === wrapperAlias)
  const suggestedVars = wrapper?.defaultVars || {}

  if (suggestedVars.footerBgColor && !email.value.vars.footerBgColor) {
    email.value.vars.footerBgColor = suggestedVars.footerBgColor
  } else {
    const newKey = `var_${Object.keys(email.value.vars).length + 1}`
    email.value.vars[newKey] = ''
  }
  email.value = { ...email.value }
}

function deleteVar(key: string) {
  if (!email.value?.vars) return
  delete email.value.vars[key]
  if (Object.keys(email.value.vars).length === 0) email.value.vars = undefined
}

// ---- Save ----
const saveEmail = async () => {
  if (saving.value || !email.value) return
  saving.value = true
  try {
    // Raw-HTML mode persists as a lone `raw` html section (migration-free); builder mode
    // serializes the section array normally.
    const payloadSections = email.value.htmlMode
      ? rawHtmlToSections(email.value.rawHtml || '')
      : serializeSections(email.value.sections)
    // Separate mobile layout: serialize the array when enabled; pass null through to clear
    // the column when disabled; undefined stays undefined (no change on partial saves).
    const payloadMobile = email.value.mobileSections == null
      ? email.value.mobileSections
      : serializeSections(email.value.mobileSections)
    const payload = { ...email.value, sections: payloadSections, mobileSections: payloadMobile }
    const saved = await $fetch<{ web_id?: string | null }>(`/api/v1/emails/${groupAlias}/${emailAlias}`, { method: "PUT", body: payload })
    // The server stores a campaign ID only when it is all digits. Take its answer, or a
    // cleared or mistyped ID would keep showing this email as published until reload.
    if (saved && 'web_id' in saved) email.value.web_id = saved.web_id ?? undefined

    if (emailsStore.map && email.value) {
      emailsStore.map[emailAlias] = { ...email.value, sections: payload.sections }
    }
    savedSnapshot = JSON.stringify(email.value)
    dirty.value = false
    previewKey.value++

    useToast().add({ title: "Saved", color: "success" })
  } catch (error) {
    useToast().add({ title: "The email did not save", description: error instanceof Error ? error.message : undefined, color: "error" })
  } finally {
    saving.value = false
  }
}

const focusLinkInput = async (sectionIndex: number, imageIndex: number) => {
  activeTab.value = 'sections'
  openIndex.value = sectionIndex
  // The tab is a route param, so wait for the navigation before looking for the field.
  await until(activeTab).toBe('sections')
  await nextTick()
  const selector = `[data-link-input="${sectionIndex}-${imageIndex}"]`
  const el = document.querySelector(selector) as HTMLElement | null
  const input = (el?.tagName === 'INPUT' ? el : el?.querySelector('input')) as HTMLInputElement | null
  if (input) {
    input.scrollIntoView({ behavior: 'smooth', block: 'center' })
    input.focus()
    input.select()
  }
}

// The email page shows Save in its header and in the unsaved-changes bar.
defineExpose({ dirty, saving, saveEmail })
</script>

<style scoped>
.editor-side {
  flex: 1 1 340px;
  min-width: 0;
}

@media (min-width: 1100px) {
  .editor-side {
    position: sticky;
    top: 74px;
    max-width: 400px;
    max-height: calc(100vh - 90px);
    overflow: auto;
  }
}

.notice {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 10px 14px;
  border: 1px solid var(--line);
  border-radius: 12px;
  background: var(--panel2);
}

/* ---- Section rows ---- */
.sec-card {
  border: 1px solid var(--line);
  border-radius: 12px;
  background: var(--panel2);
  overflow: hidden;
}

.sec-card.open {
  border-color: var(--acctext);
}

.sec-head {
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 0 8px 0 12px;
}

.sec-card.open .sec-head {
  border-bottom: 1px solid var(--line);
}

.sec-grip {
  display: flex;
  color: var(--faint);
  cursor: grab;
}

.sec-toggle {
  display: flex;
  flex: 1;
  align-items: center;
  gap: 12px;
  min-width: 0;
  min-height: 52px;
  padding: 6px;
  border: 0;
  background: none;
  color: var(--ink);
  text-align: left;
  cursor: pointer;
}

/* The label is stored uppercase ("IMAGE"); show it as a sentence-case word. */
.sec-type {
  display: inline-block;
  width: 58px;
  flex-shrink: 0;
  font-size: 13px;
  font-weight: 600;
  text-transform: lowercase;
}

.sec-type::first-letter {
  text-transform: uppercase;
}

.sec-thumbs {
  display: flex;
  flex-shrink: 0;
  gap: 4px;
}

.sec-thumb {
  display: block;
  width: 44px;
  height: 30px;
  border: 2px solid transparent;
  border-radius: 5px;
  overflow: hidden;
  background: var(--raise);
}

.sec-thumb.miss {
  border-color: var(--warn);
}

.sec-thumb img {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.sec-status {
  flex-shrink: 0;
  font-size: 13px;
  font-weight: 600;
  text-align: right;
  white-space: nowrap;
}

/* ---- Image cells ---- */
.cell {
  display: flex;
  flex-wrap: wrap;
  align-items: flex-start;
  gap: 16px;
  padding: 16px;
  border-bottom: 1px solid var(--line);
}

.cell-media {
  display: flex;
  flex: 0 0 132px;
  flex-direction: column;
  gap: 6px;
}

.cell-fields {
  display: flex;
  flex: 1 1 320px;
  flex-direction: column;
  gap: 10px;
  min-width: 0;
}

.cell-field {
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.cell-field-head {
  display: flex;
  justify-content: space-between;
  font-size: 13px;
  color: var(--muted);
}

.cell-miss {
  color: var(--warn);
  font-weight: 600;
}

/* An empty link or alt field has to look different from a filled one at a glance. */
.console-input.miss {
  border-color: var(--warn);
  box-shadow: 0 0 0 1px var(--warn);
}

/* Image-cell thumbnail. Hovering shows .cell-thumb-preview (below), teleported to body. */
.cell-thumb__box {
  display: block;
  width: 132px;
  height: 88px;
  border-radius: 8px;
  flex-shrink: 0;
  overflow: hidden;
  cursor: zoom-in;
  background: repeating-linear-gradient(45deg, var(--raise), var(--raise) 5px, var(--panel) 5px, var(--panel) 10px);
}

.cell-thumb-preview {
  position: fixed;
  z-index: 200;
  transform: translateY(-50%);
  padding: 6px;
  background: var(--panel);
  border: 1px solid var(--line2);
  border-radius: 9px;
  box-shadow: 0 14px 44px rgba(0, 0, 0, 0.5);
  pointer-events: none;
}

.cell-thumb-preview img {
  display: block;
  max-width: 340px;
  max-height: 78vh;
  width: auto;
  height: auto;
  border-radius: 4px;
}

.console-addbtn {
  height: 32px;
  padding: 0 12px;
  border: 1px solid var(--line);
  background: var(--panel);
  color: var(--ink);
  border-radius: 999px;
  font-size: 13px;
  font-weight: 600;
  cursor: pointer;
}

.console-addbtn:hover {
  background: var(--raise);
}

.console-dashed {
  height: 32px;
  padding: 0 12px;
  border: 1px dashed var(--line2);
  background: transparent;
  color: var(--muted);
  border-radius: 999px;
  font-size: 13px;
  cursor: pointer;
}

.console-dashed:hover {
  color: var(--ink);
}

.console-fmt {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  height: 32px;
  padding: 0 12px;
  border: 1px solid var(--line2);
  border-radius: 999px;
  background: transparent;
  color: var(--muted);
  font-size: 13px;
  cursor: pointer;
  user-select: none;
}

.console-fmt:hover {
  color: var(--ink);
}

.console-fmt.on {
  border-color: var(--accentline);
  color: var(--acctext);
}

.console-fmt .swatch {
  width: 18px;
  height: 18px;
  padding: 0;
  border: none;
  background: none;
  cursor: pointer;
}

.console-fmt .mini {
  width: 40px;
  border: none;
  outline: none;
  background: transparent;
  color: var(--ink);
  font-size: 13px;
}

.sec-body {
  display: flex;
  flex-direction: column;
  gap: 12px;
  padding: 14px;
}

.sec-empty {
  padding: 28px;
  border: 1px dashed var(--line2);
  border-radius: 14px;
  color: var(--muted);
  text-align: center;
}

.lint {
  margin: 10px 0 0;
  padding: 0;
  list-style: none;
  color: var(--muted);
  font-size: 13px;
}

.lint .warn {
  color: var(--warn);
}

#email-preview {
  width: 100%;
  overflow: visible;
}
</style>
